import { Slot } from '@radix-ui/react-slot'
import * as React from 'react'
import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '~in-app/lib/utils'

const buttonVariants = tv({
  base: 'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50',
  variants: {
    hasIcon: {
      true: 'gap-2',
      false: '',
    },
    fullWidth: {
      true: 'w-full',
      false: '',
    },
    color: {
      primary: 'bg-primary/90 text-primary-foreground hover:bg-primary',
      secondary: 'bg-secondary/90 text-secondary-foreground hover:bg-secondary',
      error: 'bg-error/90 text-error-foreground hover:bg-error',
      warning: 'bg-warning/90 text-warning-foreground hover:bg-warning',
      info: 'bg-info/90 text-info-foreground hover:bg-info',
      success: 'bg-success/90 text-success-foreground hover:bg-success',
      muted: 'bg-muted/90 text-muted-foreground hover:bg-muted',
    },
    size: {
      small: 'h-9 px-3 text-xs',
      medium: 'h-10 px-4',
      large: 'h-12 px-6 text-base',
    },
    variant: {
      default: 'bg-primary text-primary-foreground hover:bg-primary/90',
      text: 'bg-transparent hover:bg-opacity-20',
      contained: '',
      outlined: 'border border-current bg-transparent hover:bg-opacity-20',
      link: 'bg-transparent hover:bg-transparent hover:underline',
      ghost: 'hover:bg-accent hover:text-accent-foreground',
    },
  },
  compoundVariants: [
    {
      color: 'primary',
      variant: 'text',
      class: 'text-primary hover:bg-primary-300',
    },
    {
      color: 'secondary',
      variant: 'text',
      class: 'text-secondary hover:bg-secondary-300',
    },
    {
      color: 'error',
      variant: 'text',
      class: 'text-error hover:bg-error-300',
    },
    {
      color: 'warning',
      variant: 'text',
      class: 'text-warning hover:bg-warning-300',
    },
    {
      color: 'info',
      variant: 'text',
      class: 'text-info hover:bg-info-300',
    },
    {
      color: 'success',
      variant: 'text',
      class: 'text-success hover:bg-success-300',
    },
  ],
  defaultVariants: {
    variant: 'contained',
    color: 'primary',
    size: 'medium',
  },
})

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, color, variant, size, hasIcon, fullWidth, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, color, size, className, hasIcon, fullWidth }))}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
